import { useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { floatVal, Nullable } from '@jamesgmarks/utilities';
import { toDollarAmount } from '../../../../app-utils';
import {
  createMassDraftCreditNotes,
  previewMassCreditNotes,
} from '../../../../redux/features/credit-notes/actions';
import { showMessage } from '../../../../redux/features/messaging/actions';
import {
  IMassCreditCreatedNote,
  IMassCreditNotePlan,
  IMassCreditPlan,
  IMassCreditPlanLine,
  IMassCreditRowIssue,
  MASS_CREDIT_CSV_HEADER,
  MASS_CREDIT_PERIOD_PATTERN,
} from '../../../../interfaces/IMassCreditNotes';

/**
 * `select` — choosing a file; `review` — a validated plan is on screen;
 * `created` — the draft notes exist.
 */
type TMassCreditUploadStep = 'select' | 'review' | 'created';

export interface IMassCreditUploadModalProps {
  open: boolean;
  onClose: () => void;
}

const EXAMPLE_CSV = [
  MASS_CREDIT_CSV_HEADER,
  // A subscription is routinely billed for several buildings at once, so `buildingIds` is a
  // space-separated list and `buildingNames` a pipe-separated one. Names are optional.
  '150.00,1234,Maple Court,56789',
  '75.50,1235 1236,Oak Ridge | Pine Hall,56789',
  '200.00,9876 9877 9878,,11111',
].join('\n');

/**
 * Renders a line's buildings exactly as its description does.
 *
 * `namedBuildings` is the description's own record of what it said, so reading it here means
 * the preview cannot drift from the text it is previewing. A row the description declined to
 * name falls back to the raw id when there is only one, and to a count otherwise.
 *
 * @param line - The planned credit line.
 * @returns The cell text.
 */
const describeLineBuildings = (line: IMassCreditPlanLine) => {
  if (line.namedBuildings.length > 0) {
    return line.namedBuildings.join(', ');
  }

  if (line.buildingIds.length === 1) {
    return `${line.buildingIds[0]}`;
  }

  return `${line.buildingIds.length} properties`;
};

/** Renders a table of line-level problems, used for both errors and warnings. */
const RowIssueTable = ({ issues }: { issues: IMassCreditRowIssue[] }) => (
  <Table size="small">
    <TableHead>
      <TableRow>
        <TableCell>CSV line</TableCell>
        <TableCell>Problem</TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {issues.map((issue) => (
        <TableRow key={`${issue.lineNumber}-${issue.message}`}>
          <TableCell>{issue.lineNumber}</TableCell>
          <TableCell>
            {issue.message}
            {issue.rawRow ? <Typography variant="caption" display="block">{issue.rawRow}</Typography> : null}
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

/** Renders one planned credit note: its client-level header and every line it would carry. */
const PlannedNoteCard = ({ note }: { note: IMassCreditNotePlan }) => (
  <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
    <Typography variant="subtitle1">
      {note.clientName} — {toDollarAmount(floatVal(note.totalAmount))} {note.currencyCode}
    </Typography>
    <Typography variant="caption" display="block" mb={1}>
      Billing account {note.billingAccountId} · {note.lines.length} line(s) ·{' '}
      {note.subscriptionIds.length} subscription(s): {note.subscriptionIds.join(', ')}
    </Typography>
    {note.clientIds.length > 1 && (
      <Alert severity="info" sx={{ mb: 1 }}>
        This billing account is shared by clients {note.clientIds.join(', ')}. The note is filed
        under client {note.clientId}; each credit line keeps its own client.
      </Alert>
    )}
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>CSV line</TableCell>
          <TableCell>Subscription</TableCell>
          <TableCell>Buildings</TableCell>
          <TableCell align="right">Amount</TableCell>
          <TableCell>Description</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {note.lines.map((line) => (
          <TableRow key={line.lineNumber}>
            <TableCell>{line.lineNumber}</TableCell>
            <TableCell>{line.subscriptionId}</TableCell>
            {/* The full id list is on hover, so a counted cell is still auditable. */}
            <TableCell title={line.buildingIds.join(', ')}>{describeLineBuildings(line)}</TableCell>
            <TableCell align="right">{toDollarAmount(floatVal(line.amount))}</TableCell>
            <TableCell>{line.description}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </Paper>
);

/**
 * Renders the created notes once the drafts exist. `partialFailureMessage` is set when the run
 * stopped partway, in which case the notes listed below it are the ones that survived.
 */
const CreatedNotesSummary = ({
  createdNotes,
  partialFailureMessage,
}: {
  createdNotes: IMassCreditCreatedNote[],
  partialFailureMessage: Nullable<string>,
}) => (
  <>
    {partialFailureMessage
      ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          <AlertTitle>Stopped partway — {createdNotes.length} draft credit note(s) created</AlertTitle>
          {partialFailureMessage} The notes below already exist and were not rolled back; nothing
          was published or applied. Fix the cause, then re-upload — you will need to tick the
          duplicate checkbox, and the notes below will be created a second time unless you remove
          their rows from the CSV first.
        </Alert>
      )
      : (
        <Alert severity="success" sx={{ mb: 2 }}>
          <AlertTitle>{createdNotes.length} draft credit note(s) created</AlertTitle>
          Every note is unpublished. Nothing was published to Xero and nothing was applied to an
          invoice.
        </Alert>
      )}
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Note number</TableCell>
          <TableCell>Client</TableCell>
          <TableCell>Billing account</TableCell>
          <TableCell align="right">Lines</TableCell>
          <TableCell align="right">Total</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {createdNotes.map((note) => (
          <TableRow key={note.id}>
            <TableCell>{note.noteNumber}</TableCell>
            <TableCell>{note.clientName}</TableCell>
            <TableCell>{note.billingAccountId}</TableCell>
            <TableCell align="right">{note.lineCount}</TableCell>
            <TableCell align="right">
              {toDollarAmount(floatVal(note.totalAmount))} {note.currencyCode}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </>
);

/**
 * Uploads a CSV of `amount,buildingIds,buildingNames,subscriptionId` rows — the original
 * `amount,buildingId,subscriptionId` shape is still accepted — and creates one **draft**
 * (unpublished) credit note per client billing account and currency.
 *
 * The upload is always validated first and the resulting plan shown for review; nothing is
 * written until the user confirms. Re-uploading a CSV that has already produced notes is
 * refused unless the user explicitly opts to create them again.
 *
 * A credited period is required. It is what finds the charge, and so the invoice, each credit
 * comes off — the same linkage the bulk subscription credit modal performs — so an upload
 * without one is refused rather than written as unlinked, untaxed credits.
 */
export const MassCreditUploadModal = ({ open, onClose }: IMassCreditUploadModalProps) => {
  const [step, setStep] = useState<TMassCreditUploadStep>('select');
  const [selectedFile, setSelectedFile] = useState<Nullable<File>>(null);
  // Deliberately no default: the period decides which charge — and so which invoice — every
  // credit comes off, and a defaulted one would quietly credit against the wrong month.
  const [period, setPeriod] = useState('');
  const [plan, setPlan] = useState<Nullable<IMassCreditPlan>>(null);
  const [existingNoteNumbers, setExistingNoteNumbers] = useState<string[]>([]);
  const [allowDuplicateBatch, setAllowDuplicateBatch] = useState(false);
  const [createdNotes, setCreatedNotes] = useState<IMassCreditCreatedNote[]>([]);
  const [partialFailureMessage, setPartialFailureMessage] = useState<Nullable<string>>(null);
  const [isWorking, setIsWorking] = useState(false);

  const resetState = () => {
    setStep('select');
    setSelectedFile(null);
    setPeriod('');
    setPlan(null);
    setExistingNoteNumbers([]);
    setAllowDuplicateBatch(false);
    setCreatedNotes([]);
    setPartialFailureMessage(null);
    setIsWorking(false);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleFileSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const [file] = Array.from(event.target.files ?? []);
    if (!file) {
      return;
    }

    setSelectedFile(file);
    setPlan(null);
    setExistingNoteNumbers([]);
    setAllowDuplicateBatch(false);
    setStep('select');
  };

  /**
   * Changing the period discards the reviewed plan.
   *
   * The create call re-sends the period and the server rebuilds the plan from it, so a plan
   * reviewed under one period must not be creatable under another.
   */
  const handlePeriodChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPeriod(event.target.value);
    setPlan(null);
    setExistingNoteNumbers([]);
    setAllowDuplicateBatch(false);
    setStep('select');
  };

  const isPeriodValid = MASS_CREDIT_PERIOD_PATTERN.test(period.trim());

  const handleValidate = async () => {
    if (!selectedFile || !isPeriodValid) {
      return;
    }

    setIsWorking(true);
    try {
      const { status, message, plan: previewedPlan, existingBatchNotes } = (
        await previewMassCreditNotes(selectedFile, period.trim())
      );

      if (status === 'error' || !previewedPlan) {
        showMessage({ message, severity: 'error', asAlert: true });
        return;
      }

      setPlan(previewedPlan);
      setExistingNoteNumbers(existingBatchNotes.map((note) => note.noteNumber));
      setStep('review');
      showMessage({ message, severity: 'info' });
    } catch (error) {
      console.error('Failed to validate the mass credit CSV:', error);
      showMessage({ message: 'Failed to validate the CSV. Please try again.', severity: 'error', asAlert: true });
    } finally {
      setIsWorking(false);
    }
  };

  const handleCreate = async () => {
    if (!plan || !isPeriodValid) {
      return;
    }

    setIsWorking(true);
    try {
      const { status, message, createdNotes: notes } = await createMassDraftCreditNotes({
        csvText: plan.csvText,
        batchKey: plan.batchKey,
        period: period.trim(),
        force: allowDuplicateBatch,
      });

      // A failure part-way through still leaves the earlier notes in place (one transaction per
      // note), so those have to be shown rather than swallowed with the error.
      if (status === 'error') {
        showMessage({ message, severity: 'error', asAlert: true });

        if (notes.length > 0) {
          setCreatedNotes(notes);
          setPartialFailureMessage(message);
          setStep('created');
        }

        return;
      }

      setCreatedNotes(notes);
      setPartialFailureMessage(null);
      setStep('created');
      showMessage({ message, severity: 'success' });
    } catch (error) {
      console.error('Failed to create the mass credit draft notes:', error);
      showMessage({
        message: 'Failed to create the draft credit notes. Please try again.',
        severity: 'error',
        asAlert: true,
      });
    } finally {
      setIsWorking(false);
    }
  };

  const isBlockedByExistingBatch = existingNoteNumbers.length > 0 && !allowDuplicateBatch;
  const canCreate = (
    !!plan && plan.notes.length > 0 && !isBlockedByExistingBatch && !isWorking && isPeriodValid
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>Mass Credit Upload (CSV)</DialogTitle>
      <DialogContent>
        {isWorking && <LinearProgress sx={{ mb: 2 }} />}

        {step !== 'created' && (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              <AlertTitle>Draft credit notes only</AlertTitle>
              Rows are grouped into one unpublished credit note per client billing account and
              currency. Each credit is linked to the invoice it comes off, and a row whose
              invoice cannot be found in the credited period is reported below as an error
              rather than credited. Nothing is published to Xero and nothing is applied.
            </Alert>

            <Typography variant="subtitle2">Expected format</Typography>
            <Box component="pre" sx={{ p: 1, mb: 2, backgroundColor: 'action.hover', overflowX: 'auto' }}>
              {EXAMPLE_CSV}
            </Box>

            <Stack direction="row" spacing={2} alignItems="center" mb={2}>
              <TextField
                label="Credited period"
                placeholder="YYYY-MM"
                size="small"
                required
                value={period}
                onChange={handlePeriodChanged}
                disabled={isWorking}
                error={period.trim().length > 0 && !isPeriodValid}
                helperText={
                  period.trim().length > 0 && !isPeriodValid
                    ? 'Use YYYY-MM, e.g. 2026-07'
                    : 'The month being credited — it decides which invoice each credit comes off'
                }
              />
              <Button variant="outlined" component="label" disabled={isWorking}>
                Choose CSV
                <input type="file" accept=".csv" hidden onChange={handleFileSelected} />
              </Button>
              <Typography variant="body2">{selectedFile?.name ?? 'No file selected'}</Typography>
              <Button
                variant="contained"
                onClick={handleValidate}
                disabled={!selectedFile || !isPeriodValid || isWorking}
              >
                Validate
              </Button>
            </Stack>
          </>
        )}

        {step === 'review' && plan && (
          <>
            {existingNoteNumbers.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <AlertTitle>This CSV has already been uploaded</AlertTitle>
                It already created credit note(s) {existingNoteNumbers.join(', ')}. Creating again
                will produce a second set of drafts.
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={allowDuplicateBatch}
                      onChange={(event) => setAllowDuplicateBatch(event.target.checked)}
                    />
                  )}
                  label="Create duplicate credit notes anyway"
                />
              </Alert>
            )}

            <Typography variant="h6">
              {plan.notes.length} draft credit note(s) from {plan.totals.rowCount} CSV row(s)
            </Typography>
            <Typography variant="body2" mb={2}>
              {plan.totals.amountByCurrency
                .map((total) => `${toDollarAmount(floatVal(total.totalAmount))} ${total.currencyCode}`)
                .join(' · ') || 'No amounts'}
            </Typography>

            {plan.rowErrors.length > 0 && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <AlertTitle>
                  {plan.rowErrors.length} row(s) will be skipped
                </AlertTitle>
                <RowIssueTable issues={plan.rowErrors} />
              </Alert>
            )}

            {plan.warnings.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <AlertTitle>{plan.warnings.length} warning(s)</AlertTitle>
                <RowIssueTable issues={plan.warnings} />
              </Alert>
            )}

            {plan.notes.map((note) => (
              <PlannedNoteCard key={`${note.billingAccountId}-${note.currencyCode}`} note={note} />
            ))}
          </>
        )}

        {step === 'created' && (
          <CreatedNotesSummary
            createdNotes={createdNotes}
            partialFailureMessage={partialFailureMessage}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isWorking}>
          {step === 'created' ? 'Done' : 'Cancel'}
        </Button>
        {step === 'review' && (
          <Button variant="contained" onClick={handleCreate} disabled={!canCreate}>
            Create {plan?.notes.length ?? 0} draft credit note(s)
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
